import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Cloud,
  Cpu,
  BarChart3,
  LayoutDashboard,
  Smartphone,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  type LucideIcon,
} from 'lucide-react';
import { homeFeatureCards } from './data';

const iconMap: Record<(typeof homeFeatureCards)[number]['icon'], LucideIcon> = {
  cloud: Cloud,
  cpu: Cpu,
  chart: BarChart3,
  layout: LayoutDashboard,
  smartphone: Smartphone,
  refresh: RefreshCw,
};

export const HomeFeatureCarousel: React.FC = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanPrev(scrollLeft > 8);
    setCanNext(scrollLeft + clientWidth < scrollWidth - 8);
  }, []);

  const scrollByDir = (dir: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.min(580, el.clientWidth * 0.9), behavior: 'smooth' });
  };

  useEffect(() => {
    updateArrows();
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateArrows]);

  return (
    <section className="io-home-features" aria-labelledby="io-home-features-heading">
      <div className="io-home-features__head">
        <div className="io-home-features__head-text">
          <h2 id="io-home-features-heading" className="io-home-features__heading">
            Complete IoT Platform for Smart Device Manufacturers
          </h2>
          <p className="io-home-features__lead">
            End-to-end solution for building, managing, and scaling your smart device ecosystem with
            enterprise-grade infrastructure
          </p>
        </div>
        <div className="io-home-features__arrows">
          <button
            type="button"
            className="io-home-icon-btn"
            aria-label="Scroll feature cards left"
            disabled={!canPrev}
            onClick={() => scrollByDir(-1)}
          >
            <ChevronLeft size={28} strokeWidth={1.5} />
          </button>
          <button
            type="button"
            className="io-home-icon-btn"
            aria-label="Scroll feature cards right"
            disabled={!canNext}
            onClick={() => scrollByDir(1)}
          >
            <ChevronRight size={28} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div
        ref={scrollerRef}
        className="io-home-features__scroller"
        onScroll={updateArrows}
        tabIndex={0}
        role="region"
        aria-label="Platform capabilities"
      >
        {homeFeatureCards.map((card) => {
          const Icon = iconMap[card.icon];
          return (
            <article
              key={card.id}
              className="io-home-feature-card"
              style={{ background: card.cardBg }}
            >
              <div className="io-home-feature-card__head-row">
                <div className="io-home-feature-card__icon-slot">
                  <Icon size={24} strokeWidth={1.5} aria-hidden />
                </div>
                <h3 className="io-home-feature-card__title">{card.title}</h3>
              </div>
              <p className="io-home-feature-card__body">{card.description}</p>
              <div className="io-home-feature-card__media-wrap">
                {card.mediaImage ? (
                  <img
                    src={card.mediaImage}
                    alt=""
                    className="io-home-feature-card__photo"
                  />
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
};
